/**
 * Highlight containment — keeps the rangy text-highlighter and the annotation
 * system strictly inside the pure v-html "islands" of the quiz interface so
 * they can never mutate Vue-managed DOM nodes.
 *
 * WHY THIS EXISTS (read before changing anything here):
 * `#explanation` used to be a registered rangy highlight container, but it is
 * NOT a pure v-html island — it wraps the `#first-explanation` island PLUS
 * Vue-managed children (References/links/media v-for blocks with {{ }}
 * interpolations). A highlight applied across that boundary wraps/splits/merges
 * text nodes that Vue owns. The next Vue patch then throws mid-way
 * (insertBefore NotFoundError), and because Vue records `instance.subTree`
 * BEFORE patching, the swallowed error leaves a half-mounted tree — every
 * later update/unmount of it throws again ("emitsOptions" null / "bum"
 * destructure / parentNode null) until full page reload. This was the largest
 * error cluster in Sentry (thousands of events per week).
 *
 * The pure islands (children rendered ONLY via v-html — safe to mutate):
 *   #questionText, #questionAbstract, #first-explanation
 *
 * DATA-SAFETY CONTRACT (do not weaken):
 * - The stored blob format stays byte-compatible with rangy-highlighter
 *   serialize(): "type:textContent|start$end$id$className$containerElementId".
 *   The server (QuizController::isRealHighlight) DELETES the row for any
 *   payload not starting with "type:textContent|" — never emit anything else.
 * - Entries we cannot safely render right now (legacy container ids, offsets
 *   that no longer fit, containers not currently in the DOM, unknown class
 *   names) are NEVER dropped from the blob: they go to a "pending" bucket and
 *   are merged back verbatim on every save (mergeSerialized). Old clients keep
 *   restoring them exactly as before.
 * - Rebased entries (legacy containerId "explanation" whose range fits fully
 *   inside #first-explanation) are re-emitted with containerId
 *   "first-explanation" — an id that exists in BOTH old and new bundles, so
 *   old clients restore them correctly too.
 *
 * Safari 12 / iOS 12: this is a standalone .js file — it is NOT reliably
 * transpiled. No optional chaining, no nullish coalescing, no ES2020+.
 *
 * Kill switches (checked live per call; flip + RELOAD for clean semantics —
 * flipping mid-question mixes restore/persist modes):
 *   localStorage 'qbank_highlight_containment' = '0' -> legacy highlight
 *     engine (mixed-container scoping, monolithic deserialize, no merge).
 *   localStorage 'qbank_vdom_breaker' = '0' -> disables the corruption
 *     recovery circuit breaker (remount/reload) independently.
 */
/* eslint-env commonjs */

var ISLAND_IDS = ['questionText', 'questionAbstract', 'first-explanation'];
var ISLAND_SELECTOR = '#questionText, #questionAbstract, #first-explanation';

// Class names with a registered rangy classApplier in qbanktestinterfacePage.vue.
// Anything else would make rangy deserialize() THROW ("No class applier found"),
// killing the whole blob's restore — such entries go to the pending bucket.
var KNOWN_CLASSES = {
  'marker-yellow': true,
  'marker-green': true,
  'marker-blue': true,
  'marker-pink': true,
  'marker-orange': true,
  highlight: true,
};

var BLOB_HEADER = 'type:textContent';

function isEnabled() {
  try {
    return window.localStorage.getItem('qbank_highlight_containment') !== '0';
  } catch (e) {
    return true;
  }
}

/* ------------------------------------------------------------------ */
/* Pure blob functions (no DOM) — unit-tested in tests/               */
/* ------------------------------------------------------------------ */

/**
 * Parse a stored rangy blob into entries. Returns null when the blob is not
 * a rangy textContent serialization (caller must then leave it untouched).
 * Empty segments are skipped. Extra "$" fields (serializeHighlightText mode)
 * are preserved via `raw`.
 */
function parseBlob(blob) {
  if (typeof blob !== 'string') return null;
  var parts = blob.split('|');
  if (!/^type:textContent$/.test(parts[0])) return null;
  var entries = [];
  for (var i = 1; i < parts.length; i++) {
    var raw = parts[i];
    if (!raw) continue;
    var f = raw.split('$');
    entries.push({
      start: Number(f[0]),
      end: Number(f[1]),
      id: f[2],
      className: f[3],
      containerId: f.length > 4 && f[4] !== undefined ? f[4] : '',
      raw: raw,
    });
  }
  return entries;
}

function isValidRange(entry) {
  return (
    isFinite(entry.start) &&
    isFinite(entry.end) &&
    entry.start >= 0 &&
    entry.end > entry.start
  );
}

/**
 * Split parsed entries into { apply: [rawString], pending: [rawString] } given
 * a snapshot of the containers currently in the DOM. Pure — containerInfo is
 * built by getContainerInfo() (DOM) or a fixture (tests).
 *
 * containerInfo shape:
 *   { questionText: {len}, questionAbstract: {len}, 'first-explanation': {len},
 *     explanation: {delta, islandLen} }   — keys absent when not in the DOM.
 */
function sanitizeEntries(entries, containerInfo) {
  var apply = [];
  var pending = [];
  for (var i = 0; i < entries.length; i++) {
    var e = entries[i];

    if (!isValidRange(e) || !KNOWN_CLASSES[e.className]) {
      pending.push(e.raw);
      continue;
    }

    if (e.containerId === 'explanation') {
      // Legacy mixed-container entry: keep it ONLY if it fits entirely inside
      // the #first-explanation island, rebased to island-relative offsets.
      // Entries crossing into the (removed-from-scope) Vue-managed region are
      // exactly the crash-arming ones — never rendered, but preserved.
      var exp = containerInfo.explanation;
      if (
        exp &&
        e.start >= exp.delta &&
        e.end <= exp.delta + exp.islandLen
      ) {
        apply.push(
          (e.start - exp.delta) +
            '$' +
            (e.end - exp.delta) +
            '$' +
            e.id +
            '$' +
            e.className +
            '$first-explanation'
        );
      } else {
        pending.push(e.raw);
      }
      continue;
    }

    if (ISLAND_IDS.indexOf(e.containerId) !== -1) {
      var info = containerInfo[e.containerId];
      if (info && e.end <= info.len) {
        apply.push(e.raw);
      } else {
        // Container not rendered right now (e.g. explanation hidden until the
        // answer is revealed) or offsets no longer fit (question was edited).
        pending.push(e.raw);
      }
      continue;
    }

    // Unknown / empty container id. Empty ids made rangy fall back to
    // document.body-relative offsets — applying those can wrap arbitrary
    // page DOM (crash-arming). Preserve, never render.
    pending.push(e.raw);
  }
  return { apply: apply, pending: pending };
}

/**
 * Re-attach pending (un-rendered) entries to a freshly serialized blob so a
 * save can never destroy highlights we merely could not render. Byte-safe:
 * output still starts with "type:textContent|" whenever it has entries.
 */
function mergeSerialized(base, pendingRawEntries) {
  if (typeof base !== 'string' || base.indexOf(BLOB_HEADER) !== 0) return base;
  if (!pendingRawEntries || !pendingRawEntries.length) return base;
  var extras = [];
  for (var i = 0; i < pendingRawEntries.length; i++) {
    var raw = pendingRawEntries[i];
    // split('|')-derived strings can never contain '|'; guard anyway so a bad
    // value cannot corrupt the blob structure.
    if (raw && typeof raw === 'string' && raw.indexOf('|') === -1) {
      extras.push(raw);
    }
  }
  if (!extras.length) return base;
  return base + '|' + extras.join('|');
}

/* ------------------------------------------------------------------ */
/* DOM helpers                                                         */
/* ------------------------------------------------------------------ */

/**
 * Snapshot the highlight containers currently in the DOM.
 * `explanation.delta` = character offset of the #first-explanation island
 * inside #explanation (rangy offsets are Range.toString()-based, so we measure
 * the same way).
 */
function getContainerInfo(doc) {
  doc = doc || document;
  var info = {};
  var qt = doc.getElementById('questionText');
  var qa = doc.getElementById('questionAbstract');
  var fe = doc.getElementById('first-explanation');
  var exp = doc.getElementById('explanation');
  if (qt) info.questionText = { len: qt.textContent.length };
  if (qa) info.questionAbstract = { len: qa.textContent.length };
  if (fe) info['first-explanation'] = { len: fe.textContent.length };
  if (exp && fe && exp.contains(fe)) {
    try {
      var r = doc.createRange();
      r.selectNodeContents(exp);
      r.setEndBefore(fe);
      info.explanation = {
        delta: r.toString().length,
        islandLen: fe.textContent.length,
      };
    } catch (e) {
      // leave info.explanation undefined -> explanation entries stay pending
    }
  }
  return info;
}

/**
 * Sanitize a stored blob for restoring into the current DOM.
 * Returns { apply: [rawEntryString], pending: [rawEntryString] } or null when
 * the blob is not a rangy blob (caller should skip restore entirely).
 */
function sanitizeForRestore(blob, doc) {
  var entries = parseBlob(blob);
  if (entries === null) return null;
  return sanitizeEntries(entries, getContainerInfo(doc));
}

/** The island element containing `node`, or null. */
function islandOfNode(node) {
  if (!node) return null;
  var el = node.nodeType === 1 ? node : node.parentElement;
  if (!el || !el.closest) return null;
  return el.closest(ISLAND_SELECTOR);
}

/**
 * The island the current selection starts (or ends) in — the container a new
 * highlight must be scoped to. rangy then clamps the applied range to this
 * container, so wrapping physically cannot escape the island. Returns null
 * when the selection is empty/collapsed or touches no island (caller no-ops).
 */
function resolveSelectionIsland(win) {
  var w = win || window;
  var sel = w.getSelection ? w.getSelection() : null;
  if (!sel || sel.rangeCount === 0) return null;
  var range = sel.getRangeAt(0);
  if (!range || range.collapsed) return null;
  var island = islandOfNode(range.startContainer);
  if (island) return island;
  island = islandOfNode(range.endContainer);
  if (island) return island;
  // Fallback: both endpoints sit in Vue-managed nodes but the range may still
  // SPAN an island (e.g. a drag from above the stem down past the References).
  // rangy clamps the applied range to the returned container, so this is
  // corruption-safe — and it keeps the apply path consistent with the menu
  // gate (rangeTouchesFirstExplanation), avoiding a menu that no-ops.
  var doc = (range.startContainer && range.startContainer.ownerDocument) || document;
  for (var i = 0; i < ISLAND_IDS.length; i++) {
    var el = doc.getElementById(ISLAND_IDS[i]);
    if (!el) continue;
    try {
      if (range.intersectsNode(el)) return el;
    } catch (e) {
      // intersectsNode unavailable — endpoint checks above already ran
    }
  }
  return null;
}

/**
 * Does this selection range touch the #first-explanation island? Used by the
 * selection-menu handler: mouseups on the mixed #explanation wrapper only get
 * the menu when the selection actually involves island text.
 */
function rangeTouchesFirstExplanation(range, doc) {
  var d = doc || document;
  var island = d.getElementById('first-explanation');
  if (!island || !range) return false;
  try {
    return range.intersectsNode(island);
  } catch (e) {
    var s = islandOfNode(range.startContainer);
    var en = islandOfNode(range.endContainer);
    return s === island || en === island;
  }
}

/**
 * Roots to search when unwrapping annotation highlight spans. Annotation
 * spans only ever exist inside the annotation islands (all three pure v-html
 * islands since M28 added #questionAbstract as a supported annotation
 * container), so scoping the querySelectorAll there (instead of
 * document-wide) can never miss one. Legacy path (kill switch off) keeps the
 * old document-wide behavior.
 */
function annotationRoots(doc) {
  var d = doc || document;
  if (!isEnabled()) return [d];
  var roots = [];
  var qt = d.getElementById('questionText');
  var qa = d.getElementById('questionAbstract');
  var fe = d.getElementById('first-explanation');
  if (qt) roots.push(qt);
  if (qa) roots.push(qa);
  if (fe) roots.push(fe);
  return roots;
}

/* ------------------------------------------------------------------ */
/* vdom-corruption error family detection (shared with boot/sentry.js) */
/* ------------------------------------------------------------------ */

// Message shapes produced when a Vue patch/unmount walks a tree that external
// DOM surgery corrupted. Two tiers:
//
// STRICT — the property/DOM-operation name is pinned (bum / emitsOptions /
// parentNode / nextSibling / insertBefore / NotFoundError). Practically
// impossible for an unrelated app bug to produce through Vue's errorHandler.
// Used by BOTH Sentry fingerprinting and the recovery circuit breaker.
//
// BROAD — generic shapes ("Right side of assignment cannot be destructured",
// and the obfuscated-bundle form where the property name went through the
// obfuscator string table). These DO occur in the corruption cascade, but an
// unrelated null-deref whose property name was string-tabled looks identical.
// Used ONLY for Sentry fingerprint grouping (mis-grouping there is visible
// and reviewable via the corruption_pattern tag) — NEVER to trigger the
// remount/reload circuit breaker, so an unrelated recurring bug can never
// reload a student's session.
var CORRUPTION_PATTERNS_STRICT = [
  /Cannot destructure property 'bum'/,
  /emitsOptions/,
  /Cannot read propert(?:y|ies) of null \(reading '(?:parentNode|nextSibling|insertBefore)'\)/,
  /null is not an object \(evaluating '[^']*(?:\.|\[')(?:parentNode|nextSibling)/,
  /Failed to execute 'insertBefore' on 'Node'/,
  /The object can not be found here/,
];

var CORRUPTION_PATTERNS_BROAD = [
  /Right side of assignment cannot be destructured/,
  /(?:null|undefined) is not an object \(evaluating '_0x[0-9a-fA-F]+\[_0x[0-9a-fA-F]+\(0x[0-9a-fA-F]+\)\]/,
];

function matchesAny(patterns, message) {
  for (var i = 0; i < patterns.length; i++) {
    if (patterns[i].test(message)) return true;
  }
  return false;
}

/** Full family matcher — Sentry fingerprint grouping only. */
function isVdomCorruptionMessage(message) {
  if (!message || typeof message !== 'string') return false;
  return (
    matchesAny(CORRUPTION_PATTERNS_STRICT, message) ||
    matchesAny(CORRUPTION_PATTERNS_BROAD, message)
  );
}

/** Strict matcher — the only trigger for the remount/reload circuit breaker. */
function isVdomCorruptionMessageStrict(message) {
  if (!message || typeof message !== 'string') return false;
  return matchesAny(CORRUPTION_PATTERNS_STRICT, message);
}

/** Which tier matched — Sentry tag so any mis-grouping is observable. */
function corruptionPatternTier(message) {
  if (!message || typeof message !== 'string') return null;
  if (matchesAny(CORRUPTION_PATTERNS_STRICT, message)) return 'strict';
  if (matchesAny(CORRUPTION_PATTERNS_BROAD, message)) return 'broad';
  return null;
}

/**
 * Separate kill switch for the recovery circuit breaker (remount/reload):
 * localStorage 'qbank_vdom_breaker' = '0'. Independent of the highlight
 * containment switch so either mechanism can be reverted alone.
 */
function isBreakerEnabled() {
  try {
    return window.localStorage.getItem('qbank_vdom_breaker') !== '0';
  } catch (e) {
    return true;
  }
}

/**
 * The NEW "highlights as data" renderer (string-injection, text-quote anchors —
 * see highlightAnchor.js) is now the DEFAULT for everyone: it bakes <mark> into
 * the island HTML instead of doing live rangy DOM surgery, which removes the
 * vdom-corruption crash class. Disabled only when localStorage
 * 'qbank_highlight_renderer' === '0' — a per-browser kill switch: set it to '0'
 * and reload to fall back to the legacy rangy renderer.
 */
function isNewRendererEnabled() {
  try {
    return window.localStorage.getItem('qbank_highlight_renderer') !== '0';
  } catch (e) {
    return true;
  }
}

/**
 * Phase C: the "annotations as data" renderer — annotation spans baked into
 * the island HTML by renderHighlightsAsData (second injection pass via
 * highlightAnchor.injectAnnotationSpans) instead of the 300ms live-DOM
 * surgery pass. STAGED ROLLOUT — current stage: default OFF, opt-in with
 * localStorage 'qbank_annotation_renderer' = '1' (the default-ON flip commit
 * changes ANNOTATION_RENDERER_DEFAULT_ON to true, keeping '0' as the
 * permanent per-browser kill switch). HARD-GATED on isNewRendererEnabled():
 * baked annotation spans only exist on top of baked marks — with the highlight
 * renderer off, marks are live rangy spans and any island re-bake would wipe
 * them. Fails CLOSED (catch -> false), unlike the default-ON highlight flags.
 * NOW DEFAULT ON (kill switch: localStorage 'qbank_annotation_renderer' = '0').
 */
var ANNOTATION_RENDERER_DEFAULT_ON = true;

function isAnnotationRendererEnabled() {
  try {
    if (!isNewRendererEnabled()) return false;
    var v = window.localStorage.getItem('qbank_annotation_renderer');
    if (v === '0') return false;
    if (v === '1') return true;
    return ANNOTATION_RENDERER_DEFAULT_ON;
  } catch (e) {
    return false;
  }
}

module.exports = {
  BLOB_HEADER: BLOB_HEADER,
  ISLAND_SELECTOR: ISLAND_SELECTOR,
  KNOWN_CLASSES: KNOWN_CLASSES,
  isEnabled: isEnabled,
  isBreakerEnabled: isBreakerEnabled,
  isNewRendererEnabled: isNewRendererEnabled,
  isAnnotationRendererEnabled: isAnnotationRendererEnabled,
  parseBlob: parseBlob,
  sanitizeEntries: sanitizeEntries,
  mergeSerialized: mergeSerialized,
  getContainerInfo: getContainerInfo,
  sanitizeForRestore: sanitizeForRestore,
  islandOfNode: islandOfNode,
  resolveSelectionIsland: resolveSelectionIsland,
  rangeTouchesFirstExplanation: rangeTouchesFirstExplanation,
  annotationRoots: annotationRoots,
  isVdomCorruptionMessage: isVdomCorruptionMessage,
  isVdomCorruptionMessageStrict: isVdomCorruptionMessageStrict,
  corruptionPatternTier: corruptionPatternTier,
};
